import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, X } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import ConsentCheckbox from "./ConsentCheckbox";

const anyModalOpen = () =>
  !!document.querySelector('[role="dialog"], [data-testid="beta-questionnaire-dialog"]');

export default function ReviewPopup() {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [role, setRole] = useState("");
  const [comment, setComment] = useState("");
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let t;
    (async () => {
      try {
        const { data } = await api.get("/reviews/status");
        if (!data.show) return;
        t = setTimeout(async () => {
          if (anyModalOpen()) {
            await api.post("/reviews/action", { action: "defer" }).catch(() => {});
            return;
          }
          setOpen(true);
          api.post("/reviews/action", { action: "shown" }).catch(() => {});
        }, 3500);
      } catch (e) { /* not logged in */ }
    })();
    return () => clearTimeout(t);
  }, []);

  const later = () => setOpen(false);
  const dismiss = () => { api.post("/reviews/action", { action: "dismiss" }).catch(() => {}); setOpen(false); };

  const submit = async () => {
    if (!rating) return toast.error("Assegna una valutazione");
    setSaving(true);
    try {
      await api.post("/reviews", { rating, comment, role, consent_public: consent });
      toast.success("Grazie per la tua recensione! 🙏");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito");
      if (e?.response?.status === 400) setOpen(false);
    }
    setSaving(false);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" data-testid="review-popup">
      <div className="w-full max-w-md rounded-2xl bg-card shadow-2xl p-6 relative">
        <button onClick={later} data-testid="review-popup-close" className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"><X style={{ width: 18, height: 18 }} /></button>
        <h2 className="text-lg font-extrabold text-slate-900">Come ti trovi con MyQuoteFlow?</h2>
        <p className="text-sm text-slate-500 mt-1">La tua opinione ci aiuta a migliorare (bastano 20 secondi).</p>
        <div className="flex items-center gap-1.5 mt-4" data-testid="review-rating">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} data-testid={`review-star-${n}`} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setRating(n)} className="transition-transform hover:scale-110">
              <Star style={{ width: 30, height: 30 }} className={(hover || rating) >= n ? "text-amber-400" : "text-slate-200"} fill={(hover || rating) >= n ? "currentColor" : "none"} />
            </button>
          ))}
        </div>
        <Input data-testid="review-role" value={role} onChange={(e) => setRole(e.target.value)} placeholder="La tua professione (es. Web Designer)" className="mt-4" />
        <Textarea data-testid="review-comment" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Lascia un commento (opzionale)" className="mt-3" />
        <ConsentCheckbox id="review-consent" checked={consent} onChange={setConsent} />
        <div className="flex flex-wrap justify-end gap-2 mt-4">
          <Button variant="ghost" data-testid="review-never" onClick={dismiss} className="text-slate-500 mr-auto px-2">Non mostrare più</Button>
          <Button variant="outline" data-testid="review-later" onClick={later}>Più tardi</Button>
          <Button data-testid="review-submit" onClick={submit} disabled={saving} className="font-semibold">{saving ? "Invio…" : "Invia recensione"}</Button>
        </div>
      </div>
    </div>
  );
}
